import { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  checkInFailureText,
  checkInResultText,
} from "@/lib/guests/checkInFailureText";
import type { PartyCheckInErrorKind } from "@/lib/guests/checkInParty";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { IconX, IconLoader2, IconCheck } from "@tabler/icons-react";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { TCCCheckInStatus } from "@/components/TableCell/components/TCCCheckInStatus";
import {
  SHEET_INITIAL,
  SHEET_ANIMATE,
  SHEET_EXIT,
  SHEET_SPRING,
} from "@/components/Modal/sheetMotion";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";
import { usePartyCheckInWriter } from "@/lib/guests/usePartyCheckInWriter";
import {
  loadParty,
  partyHostId,
  PARTY_MAX,
  type PartyMember,
} from "@/lib/guests/party";
import type { PartyCheckInResult } from "@/lib/guests/checkInParty";
import { Guest } from "@/types/Guest";

type PCheckInPartyProps = {
  eventId: string;
  /** The row that was clicked — main guest or companion, both resolve to the party. */
  row: Guest;
  /** Direction, taken from the CLICKED row's own state. Never per person. */
  checkIn: boolean;
  onClose: () => void;
  /**
   * Ids the server actually flipped (or found already flipped), for surfaces
   * that keep their own copy of a guest — the detail drawer holds `checkedIn`
   * in local state and cannot learn about it from the guest list's rows.
   */
  onWrote?: (writtenIds: string[], checkIn: boolean) => void;
  /**
   * Device name to stamp on every write of this run (`performedByType`). The
   * guest list has no such field and leaves it to the server-side default; the
   * check-in-history doors both ask for one and must not lose it just because
   * the guest turned out to have companions.
   */
  performedByType?: string;
};

/**
 * "Check in Günter Schmitt — and the two people he brought."
 *
 * The door already works this way in the check-in app: the scan answers with
 * the whole party and each person's current state, the operator ticks who is
 * actually standing there, and one explicit list of ids is sent. Nothing infers
 * a party from a main guest on the write side, here no more than there.
 *
 * The party is re-read when the dialog opens rather than taken off the row:
 * on the flat list a row's `companions[]` are name chips with no `checkedIn`,
 * on the party-grouped list they are full rows — and either way a door device
 * may have checked someone in a minute ago.
 */
export const PCheckInParty: React.FC<PCheckInPartyProps> = ({
  eventId,
  row,
  checkIn,
  onClose,
  onWrote,
  performedByType,
}) => {
  const t = useTranslations("checkInParty");
  const tCheckIn = useTranslations("checkIn");
  const tErrors = useTranslations("errors");
  useSuppressCoach();
  // NOT `useGuests()` — that throws without a `GuestsProvider`, and the two
  // check-in-history doors that open this sheet render on a page without one.
  const checkInGuests = usePartyCheckInWriter(eventId);

  const [members, setMembers] = useState<PartyMember[] | null>(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // The CODE, not the sentence: `useTranslations` hands back a fresh function
  // on some renders, and an effect that depended on one would re-run — which
  // here means re-reading the party and throwing away the operator's ticks.
  const [loadErrorCode, setLoadErrorCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<Map<string, PartyCheckInResult> | null>(
    null,
  );
  /**
   * A ref as well as state (`TCCAction.tsx:64-66`): two clicks inside one tick
   * read the same state value, so state alone cannot stop a double submit —
   * and neither can the `disabled` attribute it drives.
   */
  const submitRef = useRef(false);
  const [stopped, setStopped] = useState<PartyCheckInErrorKind | null>(null);
  /** Non-failed writes of the LAST run — the numerator of "1 of 3". */
  const [lastRunDone, setLastRunDone] = useState(0);
  /** How many people the last run set out to move — the denominator of "1 of 3". */
  const [attempted, setAttempted] = useState(0);

  const hostId = partyHostId(row);

  useEffect(() => {
    let cancelled = false;
    if (!hostId) {
      setLoadErrorCode("unknown");
      return;
    }
    (async () => {
      try {
        const party = await loadParty(eventId, hostId);
        if (cancelled) return;
        setMembers(party.members);
        setHiddenCount(party.hiddenCount);
        // Everyone is ticked except whoever is already where this click would
        // put them — the same eligibility the server applies to a batch, so the
        // dialog never proposes a write the backend would refuse as a duplicate.
        setSelected(
          new Set(
            party.members
              .filter((m) => (checkIn ? m.checkedIn !== true : m.checkedIn === true))
              .map((m) => m.guestId),
          ),
        );
      } catch (e) {
        if (cancelled) return;
        // Fail loudly and stay open: a dialog that opened on half a party would
        // offer to check in a subset while looking complete.
        setLoadErrorCode(e instanceof Error ? e.message : "unknown");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, hostId, checkIn]);

  const loadError = loadErrorCode
    ? tErrors.has(loadErrorCode)
      ? tErrors(loadErrorCode)
      : tErrors("unknown")
    : null;

  const tooLarge = (members?.length ?? 0) > PARTY_MAX;

  const selectableIds = useMemo(
    () =>
      (members ?? [])
        .filter((m) => (checkIn ? m.checkedIn !== true : m.checkedIn === true))
        .map((m) => m.guestId),
    [members, checkIn],
  );

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));

  const toggle = (guestId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(guestId)) next.delete(guestId);
      else next.add(guestId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  };

  const handleConfirm = async () => {
    if (submitRef.current || selected.size === 0 || tooLarge) return;
    submitRef.current = true;
    setSubmitting(true);
    setStopped(null);
    // Order matters at a door: the main guest first, then the companions as
    // listed, so a run that stops on a capacity block stopped at a place the
    // operator can point at.
    const ids = (members ?? [])
      .map((m) => m.guestId)
      .filter((id) => selected.has(id));

    setAttempted(ids.length);
    let report;
    try {
      report = await checkInGuests(ids, checkIn, performedByType);
    } catch (e) {
      // The runner classifies every per-person failure, so nothing normal lands
      // here — but a rejection that did would otherwise leave the button
      // spinning forever with the party in an unknown state.
      setLoadErrorCode(e instanceof Error ? e.message : "unknown");
      return;
    } finally {
      // EVERY exit, not just the failing one. Releasing the latch only in the
      // `catch` left it held on exactly the paths that keep this sheet open —
      // a capacity warning, a partial failure, a stop — while
      // `setSubmitting(false)` re-enabled the button. The retry the sheet
      // exists to offer did nothing, and the only way out was to close it.
      submitRef.current = false;
      setSubmitting(false);
    }
    // MERGED, not replaced. `results` is keyed per person but written per run,
    // and a second run carries only the members still selected — the successes
    // were pruned out. Replacing dropped everyone the first run got in: they
    // lost the tick and fell back to `members`, which no write refreshes, and
    // read "Not Checked In" while standing inside.
    setResults((prev) => {
      const next = new Map(prev ?? []);
      for (const r of report.results) next.set(r.guestId, r);
      return next;
    });
    setStopped(report.stopped ?? null);
    // From THIS run. The per-row ticks read the MERGED map (so they survive a
    // retry), and pairing that cumulative count with a per-run denominator
    // printed "2 of 1 checked in".
    setLastRunDone(report.results.filter((r) => r.status !== "failed").length);
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of report.results) {
        if (r.status !== "failed") next.delete(r.guestId);
      }
      return next;
    });
    onWrote?.(
      report.results.filter((r) => r.status !== "failed").map((r) => r.guestId),
      checkIn,
    );

    const everyoneDone =
      report.results.length === ids.length &&
      report.results.every((r) => r.status !== "failed");
    // …and nobody got in OVER the limit. A WARNING-mode capacity rule means the
    // writes SUCCEEDED above the cap, and closing on that rendered the note for
    // exactly as long as the exit animation: the operator over-filled the venue
    // and was told nothing. Both callers return early into this sheet, so
    // neither of their own warning slots fires either — this is the only place
    // the fact can be shown.
    const warned = report.results.some((r) => r.warning === "capacity");
    if (everyoneDone && !warned) onClose();
  };


  // The shared one. This was the last hand-written copy of the `already` /
  // warned-success / failed split — and leaving it here while exporting the
  // shared version is precisely the "written by one side, read by nowhere"
  // shape the extraction was meant to end. No `notAllowedFromParty`: this sheet
  // checks parties OUT as well, so the plural check-in-only sentence is wrong.
  const resultText = (result: PartyCheckInResult | undefined) =>
    checkInResultText(result, {
      checkIn: tCheckIn,
      errors: tErrors,
      party: t,
    });

  if (typeof window === "undefined") return null;

  return createPortal(
    <motion.div
      className="fixed left-0 top-0 bg-black/50 flex cursor-default items-end sm:items-center justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none h-dvh w-screen z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      // React portals bubble through the REACT tree, not the DOM one: this
      // sheet is rendered from inside a table cell whose row opens the guest
      // drawer on click. Without this, every tick inside the dialog would also
      // open that drawer behind it.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        // Never dismiss mid-write: unmounting while the requests are in flight
        // would hide the outcome of a party that is half checked in.
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <motion.div
        className="w-full sm:w-[420px] max-h-[85dvh] surface-primary rounded-t-2xl sm:rounded-2xl flex flex-col shadow-xl"
        initial={SHEET_INITIAL}
        animate={SHEET_ANIMATE}
        exit={SHEET_EXIT}
        transition={SHEET_SPRING}
      >
        <div className="flex items-center justify-between px-6 py-4 shrink-0">
          <h2 className="h3">{checkIn ? t("titleCheckIn") : t("titleCheckOut")}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label={t("cancel")}
            className="p-1.5 rounded-lg hover:surface-tertiary transition-colors disabled:opacity-50 focus-ring"
          >
            <IconX size={20} className="font-secondary" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-2">
          {!members && !loadError && (
            <div className="flex justify-center py-8">
              <IconLoader2 size={20} className="animate-spin font-tertiary" />
            </div>
          )}

          {loadError && <p className="body-3-regular font-error py-2">{loadError}</p>}

          {members && (
            <>
              {!tooLarge && selectableIds.length > 1 && (
                <div className="flex items-center pb-2">
                  <CheckboxWithValidation
                    size={16}
                    isChecked={allSelected}
                    isIndeterminate={!allSelected && someSelected}
                    disabled={submitting}
                    onCheckChangeAction={toggleAll}
                    testId="root-party-select-all"
                  >
                    <span className="body-3-medium font-secondary">
                      {allSelected ? t("selectNone") : t("selectAll")}
                    </span>
                  </CheckboxWithValidation>
                </div>
              )}

              <div className="flex flex-col">
                {members.map((member) => {
                  const inTargetState = checkIn
                    ? member.checkedIn === true
                    : member.checkedIn !== true;
                  const result = results?.get(member.guestId);
                  const line = resultText(result);
                  return (
                    <div
                      key={member.guestId}
                      className="flex items-center gap-3 px-1 py-2.5"
                    >
                      <CheckboxWithValidation
                        size={16}
                        isChecked={selected.has(member.guestId)}
                        // Already where this click would put them: nothing to do,
                        // and the server would answer 409 for it.
                        disabled={inTargetState || submitting || tooLarge}
                        onCheckChangeAction={() => toggle(member.guestId)}
                        testId={`root-party-member-${member.guestId}`}
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <p
                          className={`body-2-medium truncate ${
                            inTargetState ? "font-tertiary" : "font-primary"
                          }`}
                        >
                          {[member.firstName, member.lastName]
                            .filter(Boolean)
                            .join(" ") || member.guestId}
                          {member.isMainGuest && (
                            <span className="body-3-regular font-tertiary">
                              {" "}
                              · {t("mainGuest")}
                            </span>
                          )}
                        </p>
                        {line && (
                          <span
                            className={`body-4-regular ${
                              result?.status === "failed"
                                ? "font-error"
                                : result?.warning === "capacity"
                                  ? "font-orange"
                                  : "font-secondary"
                            }`}
                          >
                            {line}
                          </span>
                        )}
                      </div>
                      {result && result.status !== "failed" ? (
                        // Every non-failed write earns the tick, a warned one
                        // included: an over-capacity check-in IS a check-in.
                        // Withholding it here left the sheet — which is now
                        // held open precisely to report that fact — showing
                        // "Not Checked In" from a `members` array no write ever
                        // refreshes, next to a companion with a green tick.
                        <IconCheck size={16} className="font-success" data-testid="party-member-done" />
                      ) : (
                        // `checkedIn` absent means BOTH "never scanned" and "a
                        // CUSTOM role's field allowlist hides it", and nothing
                        // on the row can tell them apart — JSON-B omits a null
                        // `Boolean`, so an unscanned guest arrives with the key
                        // missing, which in a check-IN sheet is most rows. A
                        // previous attempt rendered a dash for that case and so
                        // withheld the state from the ordinary majority to
                        // avoid over-claiming for a rare minority. `Not Checked
                        // In` is what the guest list shows for the same value,
                        // and being consistent with it beats being differently
                        // wrong here. (`TCCCheckInStatus` has no "unknown"
                        // state — an earlier comment claimed it did.)
                        <TCCCheckInStatus value={member.checkedIn ?? null} />
                      )}
                    </div>
                  );
                })}
              </div>

              {hiddenCount > 0 && (
                <p className="body-4-regular font-tertiary pt-2">
                  {t("hiddenCompanions", { count: hiddenCount })}
                </p>
              )}

              {tooLarge && (
                <p className="body-3-regular font-error pt-2">
                  {t("tooLarge", { max: PARTY_MAX })}
                </p>
              )}

              {stopped && (
                <p className="body-3-regular font-error pt-2">
                  {stopped === "capacity"
                    ? t("stoppedCapacity", {
                        done: lastRunDone,
                        total: attempted,
                      })
                    : checkInFailureText(stopped ?? undefined, {
                        checkIn: tCheckIn,
                        errors: tErrors,
                        party: t,
                      })}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 shrink-0">
          <SecondaryButton
            size="small"
            width="w-max"
            text={t("cancel")}
            onClick={onClose}
            disabled={submitting}
          />
          <PrimaryButton
            size="small"
            width="w-max"
            text={
              checkIn
                ? t("confirmCheckIn", { count: selected.size })
                : t("confirmCheckOut", { count: selected.size })
            }
            onClick={handleConfirm}
            disabled={submitting || selected.size === 0 || tooLarge || !members}
            loading={submitting}
          />
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
};
